import type { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { GridCellCheckboxRenderer, useGridApiContext } from "@mui/x-data-grid-premium";
import type { GridRenderCellParamsPremium } from "@mui/x-data-grid-premium/typeOverloads";
import { isGroupOrPinnedRowNodeType } from "@entrilia/shared/components/grid/gridHelper";
import { getSelectedRowIds } from "@entrilia/shared/utilities/dataGridApiHelper";

interface Props {
  params: GridRenderCellParams;
  groupsSelectionOnly?: boolean;
}

type ExtendedGridRenderCellParams = GridRenderCellParams &
  GridRenderCellParamsPremium & {
    indeterminate?: boolean;
    checked?: boolean;
    disabled?: boolean;
    onClick?: (e: MouseEvent) => void;
  };

const CheckboxGridCell = ({ params, groupsSelectionOnly }: Props) => {
  const gridApi = useGridApiContext();

  if (!isGroupOrPinnedRowNodeType(params.rowNode)) {
    return groupsSelectionOnly ? <></> : <GridCellCheckboxRenderer {...params} />;
  }

  const selectionLookup = getSelectedRowIds(gridApi);

  const children = gridApi.current?.getRowGroupChildren({
    groupId: params.rowNode.id,
    applyFiltering: true,
    applySorting: true,
  });

  const indeterminate =
    children?.some((child) => selectionLookup.has(child)) && children?.some((child) => !selectionLookup.has(child));

  const checked = children?.every((child) => selectionLookup.has(child));

  const extraData: ExtendedGridRenderCellParams = {
    ...params,
    disabled: false,
    onClick: (e) => {
      if (isGroupOrPinnedRowNodeType(params.rowNode)) {
        if (children) {
          gridApi.current?.selectRows(children, indeterminate || !checked);
        }
        e.preventDefault();
      }
    },
    indeterminate,
    checked,
  };

  return <GridCellCheckboxRenderer {...extraData} />;
};

export default CheckboxGridCell;
